import gql from "tagged-template-noop";

/**
 * GraphQL fragment for bag shipping fields.
 * This fragment defines the structure of shipping-related data in a bag.
 * @typedef {Object} BagShippingFields
 * @property {Array<Object>} methods - The available shipping methods.
 * @property {string} methods.code - The code for the shipping method.
 * @property {boolean} methods.isSelected - Indicates if the shipping method is selected.
 * @property {string} methods.displayName - The display name of the shipping method.
 * @property {string} methods.ribbonTitle - The ribbon title for the shipping method.
 * @property {number} methods.price - The price of the shipping method.
 * @property {string} methods.estimatedDeliveryText - The estimated delivery text.
 * @property {Object} methods.orderBy - The order-by details.
 * @property {string} methods.orderBy.utc - The UTC order-by time.
 * @property {Object} methods.getBy - The get-by details.
 * @property {string} methods.getBy.pacific - The Pacific time for get-by.
 * @property {string} methods.getBy.utc - The UTC time for get-by.
 * @property {string} methods.getBy.eddDisplayString - The EDD display string.
 * @property {Object} shippingAddress - The shipping address details.
 * @property {string} shippingAddress.address1 - The first line of the address.
 * @property {string} shippingAddress.address2 - The second line of the address.
 * @property {string} shippingAddress.city - The city of the address.
 * @property {string} shippingAddress.state - The state of the address.
 * @property {string} shippingAddress.postalCode - The postal code of the address.
 * @property {string} shippingAddress.country - The country of the address.
 */
const bagShippingFieldsFragment = gql`
  fragment bagShippingFields on BagShipping {
    methods {
      code
      isSelected
      displayName
      ribbonTitle
      price
      estimatedDeliveryText
      orderBy {
        utc
      }
      getBy {
        pacific
        utc
        eddDisplayString
      }
    }
    shippingAddress {
      address1
      address2
      city
      state
      postalCode
      country
    }
  }
`;

const bagSummaryFieldsFragment = gql`
  fragment bagSummaryFields on BagSummary {
    subtotal
    tax
    total
    shipping
    discount
    nonGCAmount
    amountCoveredByGiftCards
  }
`;

const bagItemFieldsFragment = gql`
  fragment bagItemFields on BagItem {
    itemId
    itemKey
    itemType
    productId
    productName
    productUrl
    quantity
    isLowStock
    lowStockMsg
    isLoyaltyProduct
    isBopis
    isFinalSale
    isHazmat
    isIneligible
    isWishlisted
    selectedSku {
      id
      styleId
      size
      color {
        colorCode
        name
        swatchUrl
      }
      image
      originalPrice
      originalPricePerItem
      salePrice
      salePricePerItem
      freeReturnShipping
      maxQuantity
      availableQuantity
      tax
      discountAmount
    }
    category {
      displayName
      unifiedId
    }
  }
`;

export const mutableBagFieldsFragment = gql`
  ${bagSummaryFieldsFragment}
  ${bagShippingFieldsFragment}
  ${bagItemFieldsFragment}

  fragment mutableBagFields on Bag {
    errorCode
    id
    buyNowOrder
    encryptedOrderId
    summary {
      ...bagSummaryFields
    }
    items(checkInventory: true, loadWishlist: true) {
      overallDeliveryType
      totalItemCount
      shipItemCount
      shipItems {
        ...bagItemFields
      }
      bopisItemCount
      bopisItems {
        ...bagItemFields
      }
      hasLoyaltyProduct
    }
    recentlyRemovedOosItems: outOfStockItems {
      sku
      displayName
      color
      size
      quantity
      price
      imageURL
    }
    davSuggestedAddress {
      address1
    }
    shipping {
      ...bagShippingFields
    }
    couponCode
  }
`;

// Fields that change when item contents change
// by removing items, changing quantity, or refreshing stock
export const bagFieldsFragment = gql`
  ${mutableBagFieldsFragment}

  fragment bagFields on Bag {
    shipping {
      methods {
        code
        isSelected
        displayName
        ribbonTitle
        price
      }
      selectedMethod {
        code
        isSelected
        displayName
        ribbonTitle
        price
      }
    }
    bopisStore {
      id
      name
      phone
      address {
        address1
        address2
        city
        state
        postalCode
        country
      }
    }
    ...mutableBagFields
  }
`;

export const initialBagQuery = gql`
  ${bagFieldsFragment}

  query initialBag(
    $moveOOSItemsFromCart: Boolean
    $cartID: String
    $requestorComponent: String
    $skipInitialShipMethodSetting: Boolean
    $bagId: String
  ) {
    bag(
      checkForLowStock: true
      requestorComponent: $requestorComponent
      checkForDelinkedSku: true
      moveOOSItemsFromCart: $moveOOSItemsFromCart
      cartID: $cartID
      skipInitialShipMethodSetting: $skipInitialShipMethodSetting
      bagId: $bagId
    ) {
      ...bagFields
    }
  }
`;

export const oosInfoFieldsFragment = gql`
  fragment oosInfoFields on OosInfo {
    id
    items {
      totalItemCount
      oosItems {
        category {
          unifiedId
        }
        itemId
        listId
        itemKey
        isFinalSale
        productId
        productName
        productUrl
        quantity
        backInStock
        isWishlisted
        isLoyaltyProduct
        isHazmat
        selectedSku {
          id
          color {
            colorCode
            name
          }
          styleId
          image
          size
          originalPricePerItem
          salePricePerItem
          maxQuantity
          availableQuantity
          originalPrice
          salePrice
        }
      }
    }
  }
`;

export const getAvailableProdSkuInfoQuery = gql`
  query getAvailableProductSkuInfoQuery($productId: String!) {
    getAvailableProductSkuInfo(productId: $productId, mainImageOnly: false) {
      sizes {
        size
        available
      }
      colors {
        colorCode
        displayName
      }
      availableSkus {
        sku
        colorCode
        size
      }
      productImages
    }
  }
`;

export const getTopRecommendedProductsQuery = gql`
  query getTopRecommendedProductsQuery($filters: [FilterInput]) {
    getTopRecommendedProducts(filters: $filters) {
      skuId
      productId
      displayName
      pdpUrl
      skuImages
      listPrice
      salePrice
      productOnSale
    }
  }
`;

export const estimateTaxesQuery = gql`
  ${bagSummaryFieldsFragment}

  mutation estimateTaxesQuery($zipCode: String!) {
    estimateTaxes(zipCode: $zipCode) {
      summary {
        ...bagSummaryFields
      }
    }
  }
`;

// TODO: This will be removed in favor of the original planned approach once we see results from AB Test
export const saveEstimateTaxesQuery = gql`
  ${bagSummaryFieldsFragment}
  ${bagShippingFieldsFragment}

  mutation saveEstimateTaxesQuery($zipCode: String!) {
    estimateTaxes(zipCode: $zipCode, save: true) {
      summary {
        ...bagSummaryFields
      }
      shipping {
        ...bagShippingFields
      }
    }
  }
`;

// save for later queries

export const addToSaveForLaterMutation = gql`
  mutation addToSaveForLaterQuery(
    $sku: String!
    $loadProductData: Boolean
    $color: String
    $size: String
    $productId: String
    $countryCode: String
  ) {
    addToSaveForLater(
      sku: $sku
      loadProductData: $loadProductData
      color: $color
      size: $size
      productId: $productId
      countryCode: $countryCode
    ) {
      productId
      sku
      color
      colorDisplayName
      countryCode
      imageUrl
      isLowStock
      lowStockQuantity
      priceDetails {
        currencySymbol
        currencyCode
        listPrice
        salePrice
        onSale
      }
      productName
      productUrl
      size
      styleNumber
      styleOption
    }
  }
`;

export const addToBagMutation = gql`
  ${mutableBagFieldsFragment}
  mutation addToBag(
    $bagId: String
    $productId: String!
    $sku: String!
    $quantity: Int!
    $requestorComponent: String
  ) {
    addToBag(
      bagId: $bagId
      productId: $productId
      sku: $sku
      quantity: $quantity
      checkForLowStock: true
      requestorComponent: $requestorComponent
    ) {
      ...mutableBagFields
    }
  }
`;

export const addGiftcardToBagMutation = gql`
  ${mutableBagFieldsFragment}
  mutation addGiftcardToBag(
    $productId: String!
    $quantity: Int!
    $amount: Float!
    $requestorComponent: String
    $bagId: String
  ) {
    addGiftCardToBag(
      productId: $productId
      quantity: $quantity
      amount: $amount
      checkForLowStock: true
      requestorComponent: $requestorComponent
      bagId: $bagId
    ) {
      ...mutableBagFields
    }
  }
`;

export const removeFromMyBagMutation = gql`
  ${mutableBagFieldsFragment}

  mutation myBagRemoveFromBag(
    $itemId: String!
    $sku: String!
    $isBopis: Boolean!
    $requestorComponent: String
    $bagId: String
  ) {
    removeFromBag(
      itemId: $itemId
      sku: $sku
      isBopis: $isBopis
      checkForLowStock: true
      requestorComponent: $requestorComponent
      bagId: $bagId
    ) {
      ...mutableBagFields
    }
  }
`;

export const removeItemFromOosMutation = gql`
  ${oosInfoFieldsFragment}
  mutation removeItemFromOOS(
    $oosItemId: String!
    $oosItemsListId: String!
    $shoppingListId: String
  ) {
    removeFromOosInfo(
      oosItemId: $oosItemId
      oosItemsListId: $oosItemsListId
      shoppingListId: $shoppingListId
    ) {
      ...oosInfoFields
    }
  }
`;

export const refreshBagStockQuery = gql`
  ${mutableBagFieldsFragment}
  query refreshBagStock(
    $moveOOSItemsFromCart: Boolean
    $requestorComponent: String
    $bagId: String
  ) {
    bag(
      checkForLowStock: true
      requestorComponent: $requestorComponent
      moveOOSItemsFromCart: $moveOOSItemsFromCart
      bagId: $bagId
    ) {
      ...mutableBagFields
    }
  }
`;

export const refreshBagQuery = gql`
  ${bagFieldsFragment}

  query myBagRefreshBagQuery {
    bag {
      ...bagFields
    }
  }
`;

export const updateBagItemMutation = gql`
  ${mutableBagFieldsFragment}
  mutation updateItem(
    $productId: String!
    $sku: String!
    $quantity: Int!
    $itemId: String!
    $isBopis: Boolean!
    $bopisStoreId: String
    $requestorComponent: String
    $bagId: String
  ) {
    updateBagItem(
      productId: $productId
      sku: $sku
      quantity: $quantity
      itemId: $itemId
      isBopis: $isBopis
      bopisStoreId: $bopisStoreId
      checkForLowStock: true
      requestorComponent: $requestorComponent
      bagId: $bagId
    ) {
      ...mutableBagFields
    }
  }
`;

export const updateBagItemSkuMutation = gql`
  ${mutableBagFieldsFragment}
  mutation updateBagItemSku(
    $productId: String!
    $sku: String!
    $quantity: Int!
    $itemId: String!
    $srcProductId: String!
    $srcSku: String!
    $srcDeliveryType: String
    $isBopis: Boolean
    $requestorComponent: String
    $bagId: String
  ) {
    updateBagItemSku(
      itemId: $itemId
      productId: $productId
      sku: $sku
      quantity: $quantity
      checkForLowStock: true
      requestorComponent: $requestorComponent
      srcProductId: $srcProductId
      srcSku: $srcSku
      srcDeliveryType: $srcDeliveryType
      isBopis: $isBopis
      bagId: $bagId
    ) {
      ...mutableBagFields
    }
  }
`;

export const setShortPaypalBrainTreePaymentInfoMutation = gql`
  ${mutableBagFieldsFragment}
  mutation setShortPaypalBrainTreePaymentInfo(
    $bagId: String
    $billingAddress: AddressInput!
    $shippingAddress: AddressInput!
    $email: String!
    $paymentNonce: String!
    $phoneNumber: String!
  ) {
    setShortPaypalBrainTreePaymentInfo(
      bagId: $bagId
      billingAddress: $billingAddress
      shippingAddress: $shippingAddress
      email: $email
      paymentNonce: $paymentNonce
      phoneNumber: $phoneNumber
      davCheck: false
    ) {
      ...mutableBagFields
    }
  }
`;

export const userProfileQuery = gql`
  query getUserProfileQuery {
    userProfile(requestSource: "checkout") {
      id
      gender
      hashedEmail
      isLoggedIn
      isLoyaltyUser
      isLoyaltyGuest
    }
  }
`;

export const addItemToBisListMutation = gql`
  mutation addItemToBisListQuery(
    $productId: String!
    $skuId: String!
    $colorCode: String
    $size: String
    $referrer: String
  ) {
    addItemToBisList(
      productId: $productId
      skuId: $skuId
      colorCode: $colorCode
      size: $size
      referrer: $referrer
    ) {
      id
      type
      attributes {
        countryCode
        productId
        createdDate
        updatedDate
      }
    }
  }
`;

/**
 * GraphQL query for retrieving saved gift cards.
 * This query fetches the list of saved gift cards associated with a user.
 *
 * @typedef {Object} GetSavedGiftCardsResponse
 * @property {Array<Object>} savedGiftCards - The list of saved gift cards.
 * @property {string} savedGiftCards.id - The unique identifier for the gift card.
 * @property {string} savedGiftCards.lastFour - The last four digits of the gift card number.
 * @property {number} savedGiftCards.balance - The remaining balance on the gift card.
 */
export const getSavedGiftCardsQuery = gql`
  query getSavedGiftCardsQuery($returnZeroBalance: Boolean) {
    savedGiftCards(
      returnZeroBalance: $returnZeroBalance
      requestSource: "checkout"
    ) {
      amountAvailable
      encryptedGiftCardId
      giftCardId
      lastFourDigits
    }
  }
`;

export const getOnelistQuery = gql`
  query getOnelistItemsQuery($limit: Int, $offset: Int) {
    getOnelistItems(loadProductData: true, limit: $limit, offset: $offset) {
      items {
        id
        type
        lists {
          addedOn
          default
          id
          count
          label
          shared
          token
        }
        productId
        productUrl
        productName
        imageUrl
        sku
        color
        colorDisplayName
        size
        isLowStock
        isHazmat
        isIneligible
        isFinalSale
        lowStockQuantity
        isAvailable
        isAvailableInDifferentColor
        styleNumber
        styleOption
        priceDetails {
          listPrice
          salePrice
        }
      }
      offset
      hasNextPage
    }
  }
`;

/**
 * GraphQL mutation for removing an item from OneList.
 * This mutation removes an item from a shared list (OneList) associated with a user.
 *
 * @typedef {Object} RemoveFromOnelistVariables
 * @property {string} itemId - The unique identifier of the item to be removed.
 *
 * @typedef {Object} RemoveFromOnelistResponse
 * @property {boolean} success - Indicates whether the item was successfully removed.
 */
export const removeFromOnelistMutation = gql`
  mutation removeItemFromOnelistQuery($id: String!, $onelistIds: [String!]) {
    removeItemFromOnelist(id: $id, onelistIds: $onelistIds) {
      wishlistId
      status
    }
  }
`;

/**
 * GraphQL query for fetching cart assets.
 * This query retrieves the assets associated with a cart, such as items, discounts, and other metadata.
 *
 * @typedef {Object} FetchCartAssetsResponse
 * @property {Array<Object>} fetchCartAssetsByToken - The list of cart assets.
 * @property {string} fetchCartAssetsByToken.id - The unique identifier for the cart asset.
 * @property {string} fetchCartAssetsByToken.type - The type of the cart asset (e.g., "default", "express").
 * @property {Object} fetchCartAssetsByToken.metadata - Additional metadata associated with the cart asset.
 */
export const fetchCartAssetsQuery = gql`
  query fetchCartAssetsQuery {
    fetchCartAssetsByToken {
      category
      payload {
        id
        encryptedId
      }
      type
    }
  }
`;

export const getOosInfoQuery = gql`
  ${oosInfoFieldsFragment}
  query getOOSInfo($shoppingListId: String) {
    oosInfo(shoppingListId: $shoppingListId) {
      ...oosInfoFields
    }
  }
`;

export const saveGiftCardToProfileMutation = gql`
  mutation saveGiftCardToProfile($cardNumber: String!, $pin: String!) {
    saveGiftCardToProfile(cardNumber: $cardNumber, pin: $pin) {
      balance
    }
  }
`;
